/**
 * Editing one vendor's keys — `docs/rowted-admin-v2.md` §5.2, board 3.
 *
 * The standard `Sheet` at 620, with three things that are particular to keys:
 *
 * **A saved secret is never revealed.** The eye reveals only what is being
 * typed *now*. There is no route that returns a stored value, so a reveal
 * button for one would be a button that cannot work — and building it as a
 * disabled control with the reason on it is more honest than leaving somebody
 * to wonder why their key looks like dots.
 *
 * **Test connection runs the unsaved values.** That is the whole reason it sits
 * beside Save rather than after it: finding out a key is wrong before the audit
 * log records a key change.
 *
 * **Live and test are two key sets, and switching the segment does not switch
 * the product.** The segment picks which set is being *edited*; **Use test
 * keys** at the bottom is what changes what every client's checkout talks to,
 * and it confirms in as many words first.
 */
import { useCallback, useMemo, useState } from 'react'
import { ApiError } from '@rowted/shared'
import { adminRequest } from '../../lib/admin-api.js'
import { Button, Checkbox, SectionLabel, Segmented, Tag } from '../../components/primitives.js'
import { Field } from '../../components/fields.js'
import { SwitchRow } from '../../components/form/rows.js'
import { Sheet } from '../../components/sheet.js'
import { Modal } from '../../components/overlays.js'
import { cn } from '../../lib/cn.js'
import {
  IconCheck, IconCopy, IconEmail, IconExternal, IconEye, IconNext, IconSend, IconSms, IconSpinner,
  IconWarn,
} from '../../frame/icons.js'
import type { StepUp } from './step-up.js'
import { formatRelative } from '../../lib/format.js'
import type { DnsRecord, IntegrationCard, IntegrationVar, RefusedCall } from './integrations.js'

/**
 * *Postmark’s last call to the events webhook was refused 4m ago — 12 since
 * one last got through. Postmark called without Rowted’s password. Press
 * Register with Postmark to put it back.* The card and the flyout say it the
 * same way.
 */
export function refusedLine(refused: RefusedCall): string {
  const hook = refused.hook === 'events' ? 'the events webhook' : refused.hook === 'inbound' ? 'the inbound webhook'
    : 'a webhook'
  const times = refused.times > 1 ? ` — ${refused.times.toLocaleString()} since one last got through` : ''
  return `Postmark’s last call to ${hook} was refused ${formatRelative(refused.at)}${times}. ${refused.sentence}`
}

export interface IntegrationFlyoutProps {
  card: IntegrationCard
  stepUp: StepUp
  /**
   * The signed-in admin's own address, for the two controls that send
   * something: board 3's "Send a test to me after connecting" names it, and a
   * checkbox that would not say where the test lands is a checkbox nobody
   * ticks.
   */
  meEmail: string
  onClose: () => void
  onSaved: () => void
}

interface TestOutcome { ok: boolean; note: string }

type Busy = 'save' | 'test' | 'register' | 'check' | 'sender' | null

export function IntegrationFlyout(
  { card, stepUp, meEmail, onClose, onSaved }: IntegrationFlyoutProps,
) {
  /** Only what somebody has typed. An untouched name is not in the request. */
  const [draft, setDraft] = useState<Record<string, string>>({})
  const [mode, setMode] = useState<'live' | 'test'>(card.mode)
  const [busy, setBusy] = useState<Busy>(null)
  const [outcome, setOutcome] = useState<TestOutcome | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [confirmMode, setConfirmMode] = useState<'live' | 'test' | null>(null)
  const [connecting, setConnecting] = useState(false)

  const dirty = Object.keys(draft).length > 0

  /*
   * Switching the segment throws the draft away rather than carrying it over.
   * Typing a live key, flicking to Test and pressing Save would otherwise file
   * the live key under test, which is the one mistake this screen must not
   * make easy.
   */
  const switchMode = useCallback((next: string) => {
    if (next === mode) return
    setDraft({})
    setOutcome(null)
    setMode(next as 'live' | 'test')
  }, [mode])

  const editable = useMemo(
    () => card.vars.filter((one) => one.source !== 'env' && !one.connected && !one.generated), [card.vars])

  const save = useCallback(() => {
    setError(null)
    setBusy('save')
    void stepUp.guard(async () => {
      await adminRequest(`/integrations/${card.integration}/secrets`, {
        method: 'PUT',
        body: { mode, values: draft },
      })
    })
      .then((ran) => { if (ran) onSaved() })
      .catch((caught: unknown) => setError(
        caught instanceof ApiError ? caught.message : 'Those keys did not save.'))
      .finally(() => setBusy(null))
  }, [card.integration, draft, mode, onSaved, stepUp])

  const test = useCallback(async () => {
    setError(null)
    setOutcome(null)
    setBusy('test')
    try {
      /*
       * The draft only. The server merges it over what is saved, so an
       * untouched key is still tried — sending blanks for the boxes nobody
       * typed in would fail for the wrong reason.
       */
      setOutcome(await adminRequest<TestOutcome>(
        `/integrations/${card.integration}/test`,
        { method: 'POST', body: { values: draft } }))
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'The test could not run.')
    } finally {
      setBusy(null)
    }
  }, [card.integration, draft])

  /**
   * The one button on this screen that reaches a real person.
   *
   * Its own action rather than `test()`, because these two *send something* —
   * an email to the admin, a text to their mobile — and they take no draft
   * values. Testing an unsaved key by mailing somebody would be a strange
   * thing to do, and Microsoft would refuse it anyway: the token comes from
   * the connection, not from the boxes above.
   */
  const sendTest = useCallback(async (which: 'graph' | 'twilio' | 'postmark', to?: string) => {
    setError(null)
    setOutcome(null)
    setBusy('test')
    try {
      /* Postmark's goes through Postmark whatever the switch says — `/test` is its Test connection. */
      const path = which === 'postmark' ? '/integrations/postmark/test-email' : `/integrations/${which}/test`
      setOutcome(await adminRequest<TestOutcome>(path, { method: 'POST', body: to ? { to } : {} }))
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'That did not send.')
    } finally {
      setBusy(null)
    }
  }, [])

  const registerWebhooks = useCallback(() => {
    setError(null)
    setBusy('register')
    void stepUp.guard(async () => {
      await adminRequest(`/integrations/stripe/webhooks/register`, { method: 'POST', body: {} })
    })
      .then((ran) => { if (ran) onSaved() })
      .catch((caught: unknown) => setError(
        caught instanceof ApiError ? caught.message : 'Stripe would not register them.'))
      .finally(() => setBusy(null))
  }, [onSaved, stepUp])

  /** The switch's confirm: which way it is about to go, or nothing open. */
  const [confirmSender, setConfirmSender] = useState<boolean | null>(null)

  /** Postmark's two that change something at Postmark or in the product: owner, step-up, then reload. */
  const postmarkAction = useCallback((what: 'register' | 'sender', on?: boolean) => {
    setError(null)
    setConfirmSender(null)
    setBusy(what)
    void stepUp.guard(async () => {
      await adminRequest(what === 'register' ? '/integrations/postmark/register' : '/integrations/postmark/sender',
        { method: 'POST', body: what === 'sender' ? { on } : {} })
    })
      .then((ran) => { if (ran) onSaved() })
      .catch((caught: unknown) => setError(caught instanceof ApiError ? caught.message
        : what === 'register' ? 'Postmark would not register Rowted.' : 'The switch did not move.'))
      .finally(() => setBusy(null))
  }, [onSaved, stepUp])

  /** **Check again** — a read, so no step-up; the card keeps what came back. */
  const [dns, setDns] = useState<{ records: DnsRecord[]; note: string | null } | null>(null)
  const checkDns = useCallback(async () => {
    setError(null)
    setBusy('check')
    try {
      const got = await adminRequest<{ dns: DnsRecord[]; dnsNote: string | null }>(
        '/integrations/postmark/dns/check', { method: 'POST', body: {} })
      setDns({ records: got.dns, note: got.dnsNote })
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'Postmark could not be asked.')
    } finally {
      setBusy(null)
    }
  }, [])

  const flipMode = useCallback((next: 'live' | 'test') => {
    setConfirmMode(null)
    setError(null)
    void stepUp.guard(async () => {
      await adminRequest('/integrations/stripe/mode', { method: 'POST', body: { mode: next } })
    })
      .then((ran) => { if (ran) onSaved() })
      .catch((caught: unknown) => setError(
        caught instanceof ApiError ? caught.message : 'The mode did not change.'))
  }, [onSaved, stepUp])

  return (
    <>
      <Sheet
        open
        onClose={onClose}
        kind="item"
        mark={<VendorTile card={card} />}
        title={card.label}
        sub="Integrations · keys are encrypted in the vault"
        width={620}
        dirty={dirty}
        changeCount={Object.keys(draft).length}
        saveLabel="Save keys"
        onSave={save}
        note={(
          <span className="text-[11.5px] text-text-faint">
            Saving asks for your sign-in code again.
          </span>
        )}
        secondary={(
          <Button onClick={() => void test()} disabled={busy !== null}>
            {busy === 'test' ? <IconSpinner size={15} /> : <IconCheck size={15} />}
            Test connection
          </Button>
        )}
        primary={(
          <Button variant="primary" onClick={save} disabled={!dirty || busy !== null}>
            {busy === 'save' ? <IconSpinner size={15} /> : null}
            Save keys
          </Button>
        )}
      >
        <div className="flex flex-col gap-4 p-4">
          {card.modes && (
            <div className="flex flex-col gap-2">
              <Segmented
                label="Which key set you are editing"
                value={mode}
                onChange={(next) => switchMode(next)}
                options={[{ value: 'live', label: 'Live' }, { value: 'test', label: 'Test' }]}
              />
              <p className="m-0 text-[11.5px] leading-[1.45] text-text-muted">
                Live and test keys are stored separately. This picks which set you are
                editing — it does not change what the product uses.
                {' '}
                <b>
                  {card.mode === 'test'
                    ? 'The product is on test keys right now.'
                    : 'The product is on live keys.'}
                </b>
              </p>
            </div>
          )}

          {error && (
            <p role="alert" className="m-0 rounded-field bg-danger-bg px-3 py-2 text-cell
                                       text-danger">
              {error}
            </p>
          )}

          {card.vars.map((variable) => (
            <SecretField
              key={variable.name}
              variable={variable}
              value={draft[variable.name] ?? ''}
              onChange={(next) => setDraft((was) => {
                const copy = { ...was }
                if (next === '') delete copy[variable.name]
                else copy[variable.name] = next
                return copy
              })}
            />
          ))}

          {editable.length === 0 && (
            <p className="m-0 rounded-field bg-surface-alt px-3 py-2 text-cell text-text-muted">
              Every one of these is set in this deployment&rsquo;s environment, so it is the
              deployment that owns them. Remove the variables if you want Rowted to hold
              the keys instead.
            </p>
          )}

          {card.integration === 'stripe' && (
            <StripeExtras
              busy={busy}
              onRegister={registerWebhooks}
              onFlip={() => setConfirmMode(card.mode === 'live' ? 'test' : 'live')}
              mode={card.mode}
            />
          )}

          {card.integration === 'graph' && (
            <GraphExtras
              card={card}
              busy={busy}
              meEmail={meEmail}
              onConnect={() => setConnecting(true)}
              onSendTest={() => void sendTest('graph')}
            />
          )}

          {card.integration === 'twilio' && (
            <TwilioExtras busy={busy} onSendTest={(to) => void sendTest('twilio', to)} />
          )}

          {card.integration === 'postmark' && (
            <PostmarkExtras
              card={card}
              busy={busy}
              meEmail={meEmail}
              records={dns?.records ?? card.dns ?? []}
              note={dns ? dns.note : card.dnsNote ?? null}
              onRegister={() => postmarkAction('register')}
              onCheck={() => void checkDns()}
              onSendTest={() => void sendTest('postmark')}
              onSwitch={(on) => setConfirmSender(on)}
            />
          )}

          {outcome && (
            <div
              className={cn('flex items-start gap-2 rounded-field px-3 py-2 text-cell',
                outcome.ok ? 'bg-accent-soft text-accent-dark' : 'bg-danger-bg text-danger')}
            >
              <span className="mt-px shrink-0">
                {outcome.ok ? <IconCheck size={14} /> : <IconWarn size={14} />}
              </span>
              <span>
                <b>{outcome.ok ? 'Test passed' : 'Test failed'}</b>
                {' · '}
                {outcome.note}
              </span>
            </div>
          )}
        </div>
      </Sheet>

      {connecting && (
        <ConnectMailboxDialog
          card={card}
          stepUp={stepUp}
          meEmail={meEmail}
          onClose={() => setConnecting(false)}
        />
      )}

      {confirmSender !== null && (
        <Modal
          open
          onClose={() => setConfirmSender(null)}
          title={confirmSender ? 'Send Rowted’s email through Postmark?' : 'Go back to Microsoft 365?'}
          width={480}
          layer={70}
          footer={(
            <>
              <Button onClick={() => setConfirmSender(null)}>Cancel</Button>
              <Button variant="primary" onClick={() => postmarkAction('sender', confirmSender)}>
                {confirmSender ? 'Send through Postmark' : 'Send through Microsoft 365'}
              </Button>
            </>
          )}
        >
          <p className="m-0 p-4 text-body leading-[1.45] text-text-body">
            {confirmSender
              ? 'From the next message, everything Rowted sends as itself — people’s emails on '
                + 'conversations, confirmations, invoices, reminders, invites and sign-in links — goes '
                + 'through Postmark, from the mail domain, and replies come back through Postmark. '
                + 'People who connected their own Outlook keep sending from Outlook.'
              : 'From the next message, Rowted’s email goes through the Microsoft 365 mailbox again, '
                + 'under the mailbox’s name. Replies to messages Postmark already sent still come '
                + 'back through Postmark.'}
          </p>
        </Modal>
      )}

      {confirmMode && (
        <Modal
          open
          onClose={() => setConfirmMode(null)}
          title={confirmMode === 'test' ? 'Put Stripe in test mode?' : 'Go back to live keys?'}
          width={460}
          layer={70}
          footer={(
            <>
              <Button onClick={() => setConfirmMode(null)}>Cancel</Button>
              <Button variant="primary" onClick={() => flipMode(confirmMode)}>
                {confirmMode === 'test' ? 'Use test keys' : 'Use live keys'}
              </Button>
            </>
          )}
        >
          <p className="m-0 p-4 text-body leading-[1.45] text-text-body">
            {confirmMode === 'test'
              ? 'Every client’s checkout will talk to Stripe’s test account, and '
                + 'every client will see a test-mode banner. No real card is charged while '
                + 'this is on, and no real payment can be taken.'
              : 'Every client’s checkout will talk to the live Stripe account again, and '
                + 'real cards will be charged. Make sure the live keys below are the right '
                + 'ones first.'}
          </p>
        </Modal>
      )}
    </>
  )
}

/** The vendor's own tile, which is why `Sheet` takes a mark. */
function VendorTile({ card }: { card: IntegrationCard }) {
  return (
    <span className="inline-flex h-9 w-9 items-center justify-center rounded-tile
                     bg-surface-alt text-[13px] font-bold text-text-muted">
      {card.label.slice(0, 2).toUpperCase()}
    </span>
  )
}

// ---------------------------------------------------------------------------
// One field
// ---------------------------------------------------------------------------

/**
 * A key box — §5.2.
 *
 * Mono, masked by default for a secret, and the eye reveals **only what is
 * being typed now**. A stored secret cannot be revealed because the server has
 * no route that returns one: the control says so rather than pretending.
 */
export function SecretField({ variable, value, onChange }: {
  variable: IntegrationVar
  value: string
  onChange: (value: string) => void
}) {
  const [shown, setShown] = useState(false)
  const [copied, setCopied] = useState(false)
  const fromEnv = variable.source === 'env'
  const stored = variable.source === 'vault'

  /* A made password is replaced by registering again, never by typing — the box says which. */
  const placeholder = variable.generated && !fromEnv
    ? (stored ? `••••••••••••${variable.last4}  ·  made by Register with Postmark`
      : 'Made when you press Register with Postmark')
    : variable.secret
      ? (stored ? `••••••••••••${variable.last4}  ·  paste a new key to replace it` : 'Not set')
      : variable.value ?? 'Not set'

  const copy = async () => {
    if (!variable.value) return
    await navigator.clipboard?.writeText(variable.value).catch(() => {})
    setCopied(true)
    setTimeout(() => setCopied(false), 1500)
  }

  return (
    <label className="flex flex-col gap-1.5">
      <span className="flex items-baseline gap-2">
        <span className="text-cell font-medium text-text">{variable.label}</span>
        {variable.required && (
          <span className="text-[11px] text-text-faint">required</span>
        )}
        {fromEnv && (
          <Tag tone="quiet" className="h-[18px] text-[10.5px]">from environment</Tag>
        )}
        {variable.connected && (
          <Tag tone={stored ? 'ok' : 'quiet'} className="h-[18px] text-[10.5px]">
            {stored ? 'connected' : 'not connected'}
          </Tag>
        )}
        {variable.generated && !fromEnv && (
          <Tag tone={stored ? 'ok' : 'quiet'} className="h-[18px] text-[10.5px]">
            {stored ? 'made by Rowted' : 'not made yet'}
          </Tag>
        )}
        {variable.source === 'default' && (
          <Tag tone="quiet" className="h-[18px] text-[10.5px]">default</Tag>
        )}
      </span>

      <span className="flex items-center gap-1.5">
        <input
          value={value}
          onChange={(event) => onChange(event.target.value)}
          disabled={fromEnv || variable.connected || variable.generated}
          placeholder={placeholder}
          type={variable.secret && !shown ? 'password' : 'text'}
          autoComplete="off"
          spellCheck={false}
          className={cn(
            'h-9 min-w-0 flex-1 rounded-field border border-line bg-field px-2.5 font-mono',
            'text-cell text-text outline-none placeholder:font-sans',
            'placeholder:text-text-faint focus:border-accent-line focus:ring-2',
            'focus:ring-accent-ring disabled:text-text-faint',
          )}
        />

        {variable.secret && (
          <button
            type="button"
            /*
             * Disabled unless something is being typed. There is no route that
             * returns a stored secret, so an eye that offered to show one would
             * be offering something the product cannot do.
             */
            disabled={value === ''}
            title={value === ''
              ? 'Saved keys can’t be shown — paste a new one to check what you typed.'
              : shown ? 'Hide' : 'Show what you typed'}
            onClick={() => setShown(!shown)}
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-button border
                       border-line bg-surface text-text-muted hover:text-text
                       disabled:text-line-strong"
          >
            <IconEye size={15} />
          </button>
        )}

        {!variable.secret && variable.value && (
          <button
            type="button"
            title="Copy"
            onClick={() => void copy()}
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-button border
                       border-line bg-surface text-text-muted hover:text-text"
          >
            {copied ? <IconCheck size={15} /> : <IconCopy size={15} />}
          </button>
        )}
      </span>

      {(variable.help || fromEnv) && (
        <span className="text-[11.5px] leading-[1.45] text-text-muted">
          {fromEnv
            ? 'Set in this deployment’s environment, which wins over the vault. '
              + 'Remove the variable to hold the key here instead.'
            : variable.help}
        </span>
      )}
    </label>
  )
}

// ---------------------------------------------------------------------------
// Stripe's own two
// ---------------------------------------------------------------------------

function StripeExtras({ busy, mode, onRegister, onFlip }: {
  busy: Busy
  mode: 'live' | 'test'
  onRegister: () => void
  onFlip: () => void
}) {
  const url = `${window.location.origin}/api/v1/webhooks/stripe`
  return (
    <div className="flex flex-col gap-2 rounded-field bg-surface-alt p-3">
      <SectionLabel>Webhooks</SectionLabel>
      <div className="flex flex-wrap items-center gap-2">
        <code className="min-w-0 flex-1 truncate rounded-field border border-line bg-surface
                         px-2.5 py-1.5 font-mono text-[11.5px] text-text-body">
          {url}
        </code>
        <Button onClick={() => void navigator.clipboard?.writeText(url).catch(() => {})}>
          <IconCopy size={15} />
          Copy
        </Button>
        <Button onClick={onRegister} disabled={busy !== null}>
          {busy === 'register' ? <IconSpinner size={15} /> : <IconExternal size={15} />}
          Register webhooks
        </Button>
      </div>
      <p className="m-0 text-[11.5px] leading-[1.45] text-text-muted">
        Rowted creates or updates its own two endpoints — billing and Connect — and keeps
        each signing secret in the vault. Running it twice updates the same two.
      </p>

      <div className="mt-1 flex flex-wrap items-center gap-2 border-t border-line-soft pt-2">
        <span className="flex-1 text-[11.5px] text-text-muted">
          The product is using <b>{mode}</b> keys.
        </span>
        <Button onClick={onFlip}>
          {mode === 'live' ? 'Switch to test keys' : 'Switch to live keys'}
        </Button>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Microsoft 365: the mailbox, and one real message
// ---------------------------------------------------------------------------

/**
 * What Microsoft 365's flyout has that the others do not — board 3.
 *
 * Above the keys are the app registration's own boxes, which this sits under:
 * the tenant, the client id and the client secret are what lets Rowted *ask*
 * for a mailbox, and they are not the mailbox. Those two facts are far enough
 * apart that putting the second one in a well of its own is the only way the
 * screen reads — somebody who has pasted three keys and pressed Save still has
 * nothing that sends, and the panel says so in as many words.
 */
function GraphExtras({ card, busy, meEmail, onConnect, onSendTest }: {
  card: IntegrationCard
  busy: Busy
  meEmail: string
  onConnect: () => void
  onSendTest: () => void
}) {
  const state = card.state as {
    mailbox?: string; sendAs?: string; replyTo?: string; connectedAt?: string
  }
  const mailbox = state.sendAs ?? state.mailbox ?? null

  return (
    <div className="flex flex-col gap-2 rounded-field bg-surface-alt p-3">
      <SectionLabel>Sending mailbox</SectionLabel>

      {mailbox
        ? (
          <>
            <div className="flex flex-wrap items-center gap-2">
              <span className="flex min-w-0 flex-1 items-center gap-2 rounded-field border
                               border-line bg-surface px-2.5 py-1.5">
                <span className="shrink-0 text-text-muted"><IconEmail size={14} /></span>
                <span className="truncate text-cell font-medium text-text">{mailbox}</span>
                <Tag tone="ok" className="ml-auto h-[18px] shrink-0 text-[10.5px]">
                  connected
                </Tag>
              </span>
              {/*
                * Send a test sits beside Reconnect rather than under it: these
                * are the two things somebody does here, and the order is the
                * order they do them in — prove it works, and if it does not,
                * connect it again.
                */}
              <Button onClick={onSendTest} disabled={busy !== null}>
                {busy === 'test' ? <IconSpinner size={15} /> : <IconEmail size={15} />}
                Send a test
              </Button>
              {/* The same going-out-to-a-vendor icon Register webhooks uses:
                  this button also ends at somebody else's sign-in page. */}
              <Button onClick={onConnect} disabled={busy !== null}>
                <IconExternal size={15} />
                Reconnect mailbox
              </Button>
            </div>
            <p className="m-0 text-[11.5px] leading-[1.45] text-text-muted">
              {state.replyTo
                ? <>Replies go to <b>{state.replyTo}</b>. </>
                : null}
              The test goes to <b>{meEmail || 'you'}</b> and nowhere else.
              {' '}
              Rowted holds a refresh token and renews it by itself
              {state.connectedAt ? ` · connected ${dayWords(state.connectedAt)}` : ''}.
            </p>
          </>
        )
        : (
          <>
            <p className="m-0 text-cell leading-[1.45] text-text-body">
              No mailbox is connected, so nothing the product sends by email leaves the
              building — invoices, quotes, reminders and sign-in links all queue instead.
              The keys above let Rowted ask Microsoft for a mailbox; this is where
              somebody signs in as one.
            </p>
            <div>
              <Button variant="primary" onClick={onConnect} disabled={busy !== null}>
                <IconEmail size={15} />
                Connect the sending mailbox
              </Button>
            </div>
          </>
        )}
    </div>
  )
}

/** "connected Sep 14" — the same short form a list cell uses. */
function dayWords(iso: string): string {
  const when = new Date(iso)
  if (Number.isNaN(when.getTime())) return 'earlier'
  return when.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
}

/**
 * Board 3's 520px dialog — the one screen in Admin that hands somebody over to
 * another company's sign-in page.
 *
 * It exists because the alternative is a button that silently opens Microsoft,
 * and the three things this says are all things a person needs *before* they
 * get there: which two permissions they are about to approve, which mailbox
 * they should sign in as, and that Rowted never sees the password. The two
 * permissions are listed rather than summarised because a consent screen that
 * asks for something the previous screen did not mention is how somebody
 * learns to click through consent screens.
 *
 * **Send as** is the box that matters. The account somebody signs in with is
 * usually their own; the mailbox that should send is usually a shared one, and
 * naming it here is what adds `Mail.Send.Shared` to the request — asking for
 * that permission when it is not needed puts an extra, scarier line on
 * Microsoft's screen for nothing.
 */
function ConnectMailboxDialog({ card, stepUp, meEmail, onClose }: {
  card: IntegrationCard
  stepUp: StepUp
  meEmail: string
  onClose: () => void
}) {
  const state = card.state as { sendAs?: string; mailbox?: string; replyTo?: string }
  const [sendAs, setSendAs] = useState(state.sendAs ?? state.mailbox ?? '')
  const [replyTo, setReplyTo] = useState(state.replyTo ?? '')
  const [testAfter, setTestAfter] = useState(true)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const go = useCallback(() => {
    setError(null)
    setBusy(true)
    void stepUp.guard(async () => {
      const started = await adminRequest<{ url: string }>(
        '/integrations/graph/connect/start', {
          method: 'POST',
          body: {
            ...(sendAs.trim() ? { sendAs: sendAs.trim() } : {}),
            ...(replyTo.trim() ? { replyTo: replyTo.trim() } : {}),
            sendTestAfter: testAfter,
          },
        })
      /*
       * A new window, not this one, and the URL exactly as the server built it.
       *
       * New window: navigating this page away would throw out every unsaved key
       * in the flyout behind this dialog. Exactly as built: the three answers
       * above are already parked server-side under this `state` token, so
       * nothing of ours belongs in a query string Microsoft is about to parse.
       *
       * The window comes back to `/admin/integrations?connected=graph…`, which
       * hands the outcome to this window and closes itself.
       */
      window.open(started.url, 'rowted-graph-connect', 'width=520,height=680')
    })
      .then((ran) => { if (ran) onClose() })
      .catch((caught: unknown) => setError(
        caught instanceof ApiError ? caught.message : 'Microsoft could not be reached.'))
      .finally(() => setBusy(false))
  }, [onClose, replyTo, sendAs, stepUp, testAfter])

  return (
    <Modal
      open
      onClose={onClose}
      layer={70}
      width={520}
      mark={(
        <span
          className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-tile"
          style={{
            color: 'rgb(var(--status-job-quoted-fg))',
            background: 'rgb(var(--status-job-quoted-bg))',
          }}
        >
          <IconEmail size={20} />
        </span>
      )}
      title="Connect the sending mailbox"
      subtitle="Microsoft 365 · Rowted Notify app"
      footerNote="Opens Microsoft in a new window"
      footer={(
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" onClick={go} disabled={busy}>
            {busy ? <IconSpinner size={15} /> : <IconNext size={15} />}
            Continue to Microsoft
          </Button>
        </>
      )}
    >
      <div className="flex flex-col gap-3">
        <p className="m-0 text-body leading-[1.5] text-text-body">
          You will sign in to Microsoft <b>as the mailbox that sends</b> and approve two
          permissions. Rowted keeps a refresh token in the vault and never sees the
          password.
        </p>

        <div className="flex flex-col gap-1.5 rounded-field border border-line-soft
                        bg-surface-alt px-3 py-2.5">
          {[
            ['Mail.Send', 'send as this mailbox'],
            ['offline_access', 'stay connected without asking again'],
          ].map(([scope, says]) => (
            <span key={scope} className="flex items-center gap-2 text-cell">
              <span className="shrink-0 text-accent"><IconCheck size={13} /></span>
              <code className="font-mono text-[12px] text-text">{scope}</code>
              <span className="text-text-muted">· {says}</span>
            </span>
          ))}
        </div>

        {error && (
          <p role="alert" className="m-0 rounded-field bg-danger-bg px-3 py-2 text-cell
                                     text-danger">
            {error}
          </p>
        )}

        <Field
          label="Send as"
          value={sendAs}
          onChange={setSendAs}
          type="email"
          placeholder="notify@yourcompany.com"
          hint={'Any shared mailbox the signed-in account may send as. Leave it blank to '
            + 'send as whoever signs in. Clients can add their own sender later, under '
            + 'their Settings.'}
        />

        <Field
          label="Reply-to"
          value={replyTo}
          onChange={setReplyTo}
          type="email"
          placeholder="support@yourcompany.com"
          hint="Where a customer's reply goes, when it should not go to the sending mailbox."
        />

        {/*
          * Ticked by default, because the failure this catches — a mailbox that
          * connects and then cannot send, usually a missing Mail.Send.Shared —
          * is otherwise found weeks later by a customer who never got an
          * invoice.
          */}
        <Checkbox
          align="start"
          checked={testAfter}
          onChange={(event) => setTestAfter(event.target.checked)}
          label={(
            <span className="flex flex-col gap-px">
              <span className="font-semibold">Send a test to me after connecting</span>
              <span className="text-[12px] text-text-muted">{meEmail || 'your address'}</span>
            </span>
          )}
        />
      </div>
    </Modal>
  )
}

// ---------------------------------------------------------------------------
// Twilio: one real text
// ---------------------------------------------------------------------------

/**
 * Board 3's 360px "Send a test SMS", inline rather than as a popover.
 *
 * The number is a box rather than a fixed line, because the admin's own mobile
 * is the right default and almost never the one somebody wants to check — a
 * carrier that drops Rowted's texts drops them for a customer's phone, not for
 * the phone of the person testing. Left empty it goes to the mobile on the
 * admin's profile, and if there is no mobile there the server says so in a
 * sentence rather than failing quietly.
 */
function TwilioExtras({ busy, onSendTest }: {
  busy: Busy
  onSendTest: (to?: string) => void
}) {
  const [to, setTo] = useState('')

  return (
    <div className="flex flex-col gap-2 rounded-field bg-surface-alt p-3">
      <SectionLabel>Send a test SMS</SectionLabel>
      <div className="flex flex-wrap items-end gap-2">
        <span className="min-w-[180px] flex-1">
          <Field
            label="To"
            value={to}
            onChange={setTo}
            type="tel"
            placeholder="Your mobile from your admin profile"
          />
        </span>
        <Button onClick={() => onSendTest(to.trim() || undefined)} disabled={busy !== null}>
          {busy === 'test' ? <IconSpinner size={15} /> : <IconSms size={15} />}
          Send a test SMS
        </Button>
      </div>
      <p className="m-0 text-[11.5px] leading-[1.45] text-text-muted">
        Twilio answers as soon as it <i>accepts</i> the message, which is a second or two
        before it is delivered. The delivery receipt arrives at the status webhook after
        that — if it never does, the webhook is the thing that is broken, not the keys.
      </p>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Postmark: its hooks, its DNS records, one real email, and the switch
// ---------------------------------------------------------------------------

const PURPOSE: Record<DnsRecord['purpose'], string> = {
  inbound: 'Replies come in',
  dkim: 'DKIM — proves the mail is ours',
  return_path: 'Return-Path — bounces come back',
  dmarc: 'DMARC — what to do with mail that is not ours',
}

/**
 * What Postmark's flyout has that the others do not (Aaron, Oct 26).
 *
 * Four wells, in the order somebody does them: register, add the records at
 * the DNS host, send one to yourself, then move the product over. The switch
 * is last on purpose — it is the one control here that changes what every
 * customer receives, and everything above it is how you know it is safe to.
 */
function PostmarkExtras({ card, busy, meEmail, records, note, onRegister, onCheck, onSendTest, onSwitch }: {
  card: IntegrationCard
  busy: Busy
  meEmail: string
  records: DnsRecord[]
  note: string | null
  onRegister: () => void
  onCheck: () => void
  onSendTest: () => void
  onSwitch: (on: boolean) => void
}) {
  const domain = card.vars.find((one) => one.name === 'MAIL_DOMAIN')?.value ?? 'mail.rowted.com'
  return (
    <>
      <div className="flex flex-col gap-2 rounded-field bg-surface-alt p-3">
        <SectionLabel>Webhooks</SectionLabel>
        {card.webhooks && (
          <>
            <CopyLine label="Inbound" value={card.webhooks.inbound} />
            <CopyLine label="Events" value={card.webhooks.events} />
          </>
        )}
        <div className="flex flex-wrap items-center gap-2">
          <Button onClick={onRegister} disabled={busy !== null}>
            {busy === 'register' ? <IconSpinner size={15} /> : <IconExternal size={15} />}
            Register with Postmark
          </Button>
          <span className="text-[11.5px] text-text-faint">
            {card.registeredAt ? `Registered ${dayWords(card.registeredAt)}` : 'Not registered yet'}
          </span>
        </div>
        {card.refused && (
          <p role="status" data-refused
            className="m-0 flex items-start gap-2 rounded-field bg-danger-bg px-2.5 py-2 text-[11.5px]
                       leading-[1.45] text-danger">
            <span className="mt-px shrink-0"><IconWarn size={14} /></span>
            <span>{refusedLine(card.refused)}</span>
          </p>
        )}
        <p className="m-0 text-[11.5px] leading-[1.45] text-text-muted">
          Rowted sets your Postmark server&rsquo;s inbound domain to <b>{domain}</b> and its inbound
          webhook, adds one events webhook — deliveries, bounces, spam complaints, opens, and addresses
          Postmark stops sending to; never link clicks — and adds the domain to the account. Both webhooks
          carry the webhook password above, which Rowted makes the first time. Running it twice updates the
          same ones.
        </p>
        <p className="m-0 text-[11.5px] leading-[1.45] text-text-muted">
          <b className="font-semibold text-text">The webhooks are Rowted&rsquo;s — leave them alone in
          Postmark.</b> Saving one in Postmark&rsquo;s own screen drops its password, and Postmark&rsquo;s calls
          are then refused. Press Register with Postmark again to put it back.
        </p>
      </div>

      <div className="flex flex-col gap-2 rounded-field bg-surface-alt p-3">
        <div className="flex items-center gap-2">
          <SectionLabel>DNS records for {domain}</SectionLabel>
          <span className="ml-auto">
            <Button onClick={onCheck} disabled={busy !== null}>
              {busy === 'check' ? <IconSpinner size={15} /> : <IconCheck size={15} />}
              Check again
            </Button>
          </span>
        </div>
        {records.map((record) => <DnsLine key={`${record.purpose}:${record.host}`} record={record} />)}
        {note && (
          <p className="m-0 rounded-field bg-surface px-2.5 py-2 text-[11.5px] leading-[1.45] text-text-muted">
            {note}
          </p>
        )}
        <p className="m-0 text-[11.5px] leading-[1.45] text-text-muted">
          Add these where rowted.com&rsquo;s DNS is hosted. All four are on {domain}; nothing about
          rowted.com itself changes, so Rowted&rsquo;s own mail stays on Microsoft 365.
        </p>
        <p data-dmarc-advice className="m-0 text-[11.5px] leading-[1.45] text-text-muted">
          <b className="font-semibold text-text">DMARC: start at p=none.</b> Read the reports that arrive at
          DMARC reports to (above) for a couple of weeks, and once they show only Postmark sending as {domain},
          change the record to p=quarantine. Leave DMARC reports to blank and the record asks for no reports.
        </p>
      </div>

      <div className="flex flex-col gap-2 rounded-field bg-surface-alt p-3">
        <SectionLabel>Send a test email</SectionLabel>
        <div className="flex flex-wrap items-center gap-2">
          <Button onClick={onSendTest} disabled={busy !== null}>
            {busy === 'test' ? <IconSpinner size={15} /> : <IconSend size={15} />}
            Send a test email
          </Button>
          <span className="text-[11.5px] text-text-muted">
            To <b>{meEmail || 'you'}</b> and nowhere else, through Postmark whatever the switch below says.
          </span>
        </div>
      </div>

      <div className="flex flex-col gap-1 rounded-field bg-surface-alt px-3 pb-2 pt-3">
        <SectionLabel>Which service sends</SectionLabel>
        <SwitchRow
          label="Rowted’s email goes through Postmark"
          sub={card.sends
            ? <>Everything Rowted sends as itself leaves from {domain} under the sender&rsquo;s own name,
              and replies come back here. People&rsquo;s own Outlook still sends from Outlook.</>
            : <>Off: Rowted&rsquo;s email goes through the Microsoft 365 mailbox, exactly as it does
              today. Turn it on once the test above arrives.</>}
          checked={Boolean(card.sends)}
          onChange={(next) => onSwitch(next)}
          disabled={busy !== null}
        />
        <p className="m-0 border-t border-line-faint pt-2 text-[11.5px] leading-[1.45] text-text-muted">
          Postmark takes up to 10 MB a message, attachments included once encoded, and 50 people a
          message — so while it sends, files past about 7 MB together go as download links that work for
          30 days, up to 100 MB on one email.
          Postmark stops sending to an address that hard-bounced or reported spam by itself; Rowted
          hears when it does, warns whoever writes to it next, and shows it on the email log — Postmark&rsquo;s
          list is still the one that refuses. Opens are watched on email a customer reads, never on a
          sign-in link, an invite or anything to staff, and link clicks never are.
        </p>
      </div>
    </>
  )
}

/** A read-only address with Copy, like the card's redirect URI. */
function CopyLine({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center gap-2">
      <span className="w-[64px] shrink-0 text-[11.5px] text-text-muted">{label}</span>
      <code className="min-w-0 flex-1 truncate rounded-field border border-line bg-surface px-2.5 py-1.5
                       font-mono text-[11.5px] text-text-body" title={value}>
        {value}
      </code>
      <Button onClick={() => void navigator.clipboard?.writeText(value).catch(() => {})}>
        <IconCopy size={15} />
        Copy
      </Button>
    </div>
  )
}

/** One record: what it is for, its type, host and value to copy, and whether it is verified. */
function DnsLine({ record }: { record: DnsRecord }) {
  const [copied, setCopied] = useState<'host' | 'value' | null>(null)
  const copy = (what: 'host' | 'value') => {
    void navigator.clipboard?.writeText(what === 'host' ? record.host : record.value)
      .then(() => { setCopied(what); setTimeout(() => setCopied(null), 1500) })
      .catch(() => {})
  }
  return (
    <div className="flex flex-col gap-1 rounded-field border border-line bg-surface px-2.5 py-2">
      <span className="flex items-center gap-2">
        <span className="font-mono text-[11px] font-semibold text-text-muted">{record.type}</span>
        <span className="text-cell font-medium text-text">{PURPOSE[record.purpose]}</span>
        <Tag
          tone={record.verified === true ? 'ok' : record.verified === false ? 'warn' : 'quiet'}
          className="ml-auto h-[18px] shrink-0 text-[10.5px]"
        >
          {record.verified === true ? 'verified' : record.verified === false ? 'not verified yet' : 'not checked'}
        </Tag>
      </span>
      <span className="grid grid-cols-[44px_minmax(0,1fr)_auto] items-center gap-x-2 gap-y-0.5 text-[11.5px]">
        <span className="text-text-faint">Host</span>
        <code className="truncate font-mono text-text-body" title={record.host}>{record.host}</code>
        <button type="button" onClick={() => copy('host')}
          className="text-[11.5px] font-semibold text-accent hover:text-accent-dark">
          {copied === 'host' ? 'Copied' : 'Copy'}
        </button>
        <span className="text-text-faint">Value</span>
        <code className="truncate font-mono text-text-body" title={record.value}>{record.value}</code>
        <button type="button" onClick={() => copy('value')}
          className="text-[11.5px] font-semibold text-accent hover:text-accent-dark">
          {copied === 'value' ? 'Copied' : 'Copy'}
        </button>
      </span>
    </div>
  )
}
